import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { tenantSettingsService } from '../services/tenantSettingsService'
import { useTenantProductType } from '../hooks/useTenantProductType'
import type { TenantSettings } from '../types/tenantSettings'
import './SettingsTenant.css'

const MIN_SESSION_TIMEOUT_MINUTES = 5
const MAX_SESSION_TIMEOUT_MINUTES = 240

export function SettingsTenant() {
  const queryClient = useQueryClient()
  const { isPptOnly } = useTenantProductType()
  const [formData, setFormData] = useState<TenantSettings | null>(null)
  const [squareTokenInput, setSquareTokenInput] = useState('')

  const { data: tenantSettings, isLoading, error } = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: () => tenantSettingsService.getCurrent(),
  })

  useEffect(() => {
    if (tenantSettings) {
      setFormData(tenantSettings)
      setSquareTokenInput('')
    }
  }, [tenantSettings])

  const updateMutation = useMutation({
    mutationFn: (data: Partial<TenantSettings>) => tenantSettingsService.update(data),
    onSuccess: (updated) => {
      queryClient.setQueryData(['tenant-settings'], updated)
      alert('Tenant settings updated successfully')
    },
  })

  const handleTimeoutChange = (value: string) => {
    const parsed = Number(value)
    if (!Number.isFinite(parsed)) return

    const clamped = Math.min(
      MAX_SESSION_TIMEOUT_MINUTES,
      Math.max(MIN_SESSION_TIMEOUT_MINUTES, parsed)
    )

    setFormData((prev) =>
      prev
        ? { ...prev, sessionTimeoutMinutes: clamped }
        : { sessionTimeoutMinutes: clamped }
    )
  }

  const handleLogoUrlChange = (value: string) => {
    setFormData((prev) => {
      if (prev) {
        return { ...prev, logoUrl: value }
      }

      return {
        sessionTimeoutMinutes: tenantSettings?.sessionTimeoutMinutes ?? MIN_SESSION_TIMEOUT_MINUTES,
        logoUrl: value,
      }
    })
  }

  const handleSmsFieldChange = (field: keyof TenantSettings, value: string | boolean | null) => {
    setFormData((prev) => {
      if (prev) {
        return { ...prev, [field]: value }
      }
      return {
        sessionTimeoutMinutes: tenantSettings?.sessionTimeoutMinutes ?? MIN_SESSION_TIMEOUT_MINUTES,
        [field]: value,
      } as TenantSettings
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData) return

    try {
      const normalizedLogoUrl = (formData.logoUrl ?? '').trim()
      const logoUrl = normalizedLogoUrl.length > 0 ? normalizedLogoUrl : null
      const squareAccessToken = squareTokenInput.trim().length > 0 ? squareTokenInput.trim() : undefined
      const provider = formData.posProvider ?? 'none'
      const hasToken = !!formData.hasSquareAccessToken || !!squareAccessToken

      if (provider === 'square') {
        if (!formData.squareAppId || formData.squareAppId.trim().length === 0) {
          alert('Square application ID is required.')
          return
        }
        if (!hasToken) {
          alert('Square access token is required.')
          return
        }
      }

      await updateMutation.mutateAsync({
        sessionTimeoutMinutes: formData.sessionTimeoutMinutes,
        ...(isPptOnly ? { modules: { leave: formData.modules?.leave === true } } : {}),
        logoUrl,
        posProvider: formData.posProvider ?? 'none',
        squareEnvironment: formData.squareEnvironment ?? 'sandbox',
        squareAppId: formData.squareAppId ?? null,
        squareAccessToken,
        smsEnabled: formData.smsEnabled ?? false,
        smsSenderId: formData.smsSenderId ?? null,
        smsOriginationNumber: formData.smsOriginationNumber ?? null,
        smsQuietHoursStart: formData.smsQuietHoursStart ?? null,
        smsQuietHoursEnd: formData.smsQuietHoursEnd ?? null,
      })
      setSquareTokenInput('')
    } catch (err: any) {
      alert(err?.message || 'Failed to update tenant settings')
    }
  }

  const handleReset = () => {
    if (tenantSettings) {
      setFormData({ ...tenantSettings })
    } else {
      setFormData(null)
    }
    setSquareTokenInput('')
  }

  if (isLoading && !formData) {
    return (
      <div className="settings-content" role="main">
        <div className="loading" role="status" aria-live="polite">
          Loading tenant settings...
        </div>
      </div>
    )
  }

  if (error && !tenantSettings) {
    return (
      <div className="settings-content" role="main">
        <div className="error" role="alert" aria-live="assertive">
          Failed to load tenant settings: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      </div>
    )
  }

  const displayData = formData || tenantSettings

  const logoPreviewUrl = (displayData?.logoUrl ?? '').trim() || '/logo@0.5x.png'
  const maskedSquareToken = displayData?.squareAccessTokenLast4
    ? `•••• ${displayData.squareAccessTokenLast4}`
    : ''

  if (!displayData) {
    return (
      <div className="settings-content" role="main">
        <div className="error" role="alert">
          No tenant settings available.
        </div>
      </div>
    )
  }

  return (
    <div className="settings-content settings-tenant" role="main">
      <div className="settings-header">
        <div>
          <h1>Tenant Settings</h1>
          <p className="settings-subtitle">
            Configure tenant-specific behaviour such as session timeouts and other security-related
            settings.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="tenant-settings-form">
        <section className="settings-section">
          <h2>Session Timeout</h2>
          <p className="section-description">
            Control how long a user can be inactive before they are automatically signed out from
            this tenant. This applies to the in-app inactivity timeout and warning dialog. The
            underlying authentication token expiry is still enforced by the identity provider.
          </p>

          <div className="form-group-inline">
            <label htmlFor="session-timeout-minutes">
              Session timeout (minutes)
            </label>
            <div className="input-with-hint">
              <input
                id="session-timeout-minutes"
                type="number"
                min={MIN_SESSION_TIMEOUT_MINUTES}
                max={MAX_SESSION_TIMEOUT_MINUTES}
                value={displayData.sessionTimeoutMinutes}
                onChange={(e) => handleTimeoutChange(e.target.value)}
              />
              <span className="field-hint">
                Allowed range: {MIN_SESSION_TIMEOUT_MINUTES}–{MAX_SESSION_TIMEOUT_MINUTES} minutes.
              </span>
            </div>
          </div>
        </section>

        {isPptOnly && (
          <section className="settings-section">
            <h2>Leave module</h2>
            <p className="section-description">
              Turn on holiday and sick leave for this clinic. Team appears in the navigation when this is on.
            </p>
            <div className="form-group-inline">
              <label htmlFor="leave-module-enabled">
                <input
                  id="leave-module-enabled"
                  type="checkbox"
                  checked={displayData.modules?.leave === true}
                  onChange={(e) =>
                    setFormData((prev) =>
                      prev
                        ? { ...prev, modules: { ...prev.modules, leave: e.target.checked } }
                        : prev
                    )
                  }
                />
                Enable leave
              </label>
            </div>
          </section>
        )}

        <section className="settings-section">
          <h2>Branding</h2>
          <p className="section-description">
            Provide a logo URL to brand the header. Leave blank to use the default logo.
          </p>

          <div className="form-group-inline">
            <label htmlFor="tenant-logo-url">Logo URL</label>
            <div className="input-with-hint">
              <input
                id="tenant-logo-url"
                type="url"
                placeholder="https://example.com/logo.png"
                value={displayData.logoUrl ?? ''}
                onChange={(e) => handleLogoUrlChange(e.target.value)}
              />
              <span className="field-hint">Accepted formats: PNG, JPG, SVG. Use a transparent background if possible.</span>
            </div>
          </div>

          <div className="tenant-logo-preview">
            <span className="tenant-logo-preview-label">Preview</span>
            <div className="tenant-logo-preview-image">
              <img src={logoPreviewUrl} alt="Tenant logo preview" />
            </div>
          </div>
        </section>

        <section className="settings-section">
          <h2>POS Provider</h2>
          <p className="section-description">
            Configure the Point of Sale provider used to sync catalog items.
          </p>

          <div className="form-group-inline">
            <label htmlFor="pos-provider">Provider</label>
            <div className="input-with-hint">
              <select
                id="pos-provider"
                value={displayData.posProvider ?? 'none'}
                onChange={(e) => setFormData((prev) => prev ? { ...prev, posProvider: e.target.value as TenantSettings['posProvider'] } : null)}
              >
                <option value="none">None</option>
                <option value="square">Square</option>
              </select>
              <span className="field-hint">Choose Square to enable catalog sync.</span>
            </div>
          </div>

          {displayData.posProvider === 'square' && (
            <div className="pos-provider-fields">
              <div className="form-group-inline">
                <label htmlFor="square-environment">Environment</label>
                <div className="input-with-hint">
                  <select
                    id="square-environment"
                    value={displayData.squareEnvironment ?? 'sandbox'}
                    onChange={(e) => setFormData((prev) => prev ? { ...prev, squareEnvironment: e.target.value as TenantSettings['squareEnvironment'] } : null)}
                  >
                    <option value="sandbox">Sandbox</option>
                    <option value="production">Production</option>
                  </select>
                  <span className="field-hint">Use Sandbox for testing.</span>
                </div>
              </div>

              <div className="form-group-inline">
                <label htmlFor="square-app-id">Application ID</label>
                <div className="input-with-hint">
                  <input
                    id="square-app-id"
                    type="text"
                    placeholder="sq0idp-..."
                    value={displayData.squareAppId ?? ''}
                    onChange={(e) => setFormData((prev) => prev ? { ...prev, squareAppId: e.target.value } : null)}
                  />
                  <span className="field-hint">Square application ID.</span>
                </div>
              </div>

              <div className="form-group-inline">
                <label htmlFor="square-access-token">Access Token</label>
                <div className="input-with-hint">
                  <input
                    id="square-access-token"
                    type="password"
                    placeholder={maskedSquareToken ? `Saved ${maskedSquareToken}` : 'Paste access token'}
                    value={squareTokenInput}
                    onChange={(e) => setSquareTokenInput(e.target.value)}
                    autoComplete="off"
                  />
                  <span className="field-hint">
                    {maskedSquareToken ? `Saved token ${maskedSquareToken}` : 'Token is stored securely.'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </section>

        <section className="settings-section">
          <h2>SMS Reminders</h2>
          <p className="section-description">
            Configure SMS sender details and quiet hours for patient recall and reminder messages.
          </p>

          <div className="form-group checkbox-group">
            <input
              id="sms-enabled"
              type="checkbox"
              checked={!!displayData.smsEnabled}
              onChange={(e) => handleSmsFieldChange('smsEnabled', e.target.checked)}
            />
            <label htmlFor="sms-enabled">Enable SMS reminders</label>
          </div>

          <div className="form-group-inline">
            <label htmlFor="sms-sender-id">SMS Sender ID</label>
            <div className="input-with-hint">
              <input
                id="sms-sender-id"
                type="text"
                placeholder="e.g., CLNICL"
                value={displayData.smsSenderId ?? ''}
                onChange={(e) => handleSmsFieldChange('smsSenderId', e.target.value)}
              />
              <span className="field-hint">Alphanumeric sender ID (where supported).</span>
            </div>
          </div>

          <div className="form-group-inline">
            <label htmlFor="sms-origination-number">Origination Number</label>
            <div className="input-with-hint">
              <input
                id="sms-origination-number"
                type="text"
                placeholder="+353..."
                value={displayData.smsOriginationNumber ?? ''}
                onChange={(e) => handleSmsFieldChange('smsOriginationNumber', e.target.value)}
              />
              <span className="field-hint">E.164 format (optional if Sender ID is used).</span>
            </div>
          </div>

          <div className="form-group-inline">
            <label htmlFor="sms-quiet-start">Quiet hours start</label>
            <div className="input-with-hint">
              <input
                id="sms-quiet-start"
                type="time"
                value={displayData.smsQuietHoursStart ?? ''}
                onChange={(e) => handleSmsFieldChange('smsQuietHoursStart', e.target.value)}
              />
              <span className="field-hint">Local practice time.</span>
            </div>
          </div>

          <div className="form-group-inline">
            <label htmlFor="sms-quiet-end">Quiet hours end</label>
            <div className="input-with-hint">
              <input
                id="sms-quiet-end"
                type="time"
                value={displayData.smsQuietHoursEnd ?? ''}
                onChange={(e) => handleSmsFieldChange('smsQuietHoursEnd', e.target.value)}
              />
              <span className="field-hint">Messages resume after this time.</span>
            </div>
          </div>
        </section>

        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleReset}
            disabled={updateMutation.isPending}
          >
            Reset
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  )
}

